import React, { useEffect, useState } from "react";
import API from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [voters, grievances, projects, beneficiaries, contacts, tours] =
          await Promise.all([
            API.get("/voters/", { params: { page: 1, per_page: 1 } }),
            API.get("/grievances/", { params: { page: 1, per_page: 100 } }),
            API.get("/development-projects/", {
              params: { page: 1, per_page: 100 },
            }),
            API.get("/beneficiary-projects/", {
              params: { page: 1, per_page: 100 },
            }),
            API.get("/contacts/workers", { params: { page: 1, per_page: 1 } }),
            API.get("/tourprograms/", { params: { page: 1, per_page: 100 } }),
          ]);

        const gList = grievances.data.data.grievances || [];
        const pList = projects.data.data.projects || [];
        const tList = tours.data.data.tours || [];

        const gByStatus = {},
          gByPriority = {},
          pByStatus = {};
        let totalBudget = 0;

        gList.forEach((g) => {
          gByStatus[g.current_status] = (gByStatus[g.current_status] || 0) + 1;
          gByPriority[g.priority] = (gByPriority[g.priority] || 0) + 1;
        });
        pList.forEach((p) => {
          pByStatus[p.status] = (pByStatus[p.status] || 0) + 1;
          totalBudget += parseFloat(p.estimated_amount || 0);
        });

        setStats({
          totalVoters: voters.data.data.pagination.total,
          totalGrievances: grievances.data.data.pagination.total,
          totalProjects: projects.data.data.pagination.total,
          totalBeneficiaries: beneficiaries.data.data.pagination.total,
          totalContacts: contacts.data.data.pagination.total,
          totalTours: tours.data.data.pagination.total,
          gByStatus,
          gByPriority,
          pByStatus,
          totalBudget,
          recentGrievances: gList.slice(0, 5),
          recentTours: tList.slice(0, 5),
        });
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const statusColors = {
    New: { bg: "#dbeafe", color: "#1d4ed8" },
    InProgress: { bg: "#fef9c3", color: "#a16207" },
    OnHold: { bg: "#f3f4f6", color: "#374151" },
    Resolved: { bg: "#dcfce7", color: "#15803d" },
    Closed: { bg: "#f1f5f9", color: "#334155" },
    Escalated: { bg: "#fee2e2", color: "#b91c1c" },
  };

  const priorityColors = {
    LOW: "#22c55e",
    MEDIUM: "#f59e0b",
    HIGH: "#f97316",
    CRITICAL: "#ef4444",
  };

  const projectStatusColors = {
    Planned: "#6366f1",
    Sanctioned: "#0ea5e9",
    Tendered: "#8b5cf6",
    "Work Started": "#f59e0b",
    "In Progress": "#f97316",
    "On Hold": "#ef4444",
    Completed: "#22c55e",
    Cancelled: "#6b7280",
  };

  const formatCurrency = (val) =>
    val >= 10000000
      ? `₹${(val / 10000000).toFixed(2)} Cr`
      : val >= 100000
        ? `₹${(val / 100000).toFixed(2)} L`
        : `₹${val.toLocaleString("en-IN")}`;

  const timeStr = time.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const dayName = time.toLocaleDateString("en-IN", { weekday: "long" });
  const dateStr = time.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  if (loading)
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <div
            className="spinner-border text-primary mb-3"
            style={{ width: 48, height: 48 }}
          ></div>
          <div className="text-muted">Loading dashboard...</div>
        </div>
      </div>
    );

  const statCards = [
    {
      label: "Total Voters",
      value: (stats.totalVoters || 0).toLocaleString("en-IN"),
      icon: "bi-people-fill",
      sub: "Registered voters",
      grad: "linear-gradient(135deg,#1a3c5e,#2563eb)",
    },
    {
      label: "Grievances",
      value: stats.totalGrievances || 0,
      icon: "bi-chat-left-text-fill",
      sub: `${stats.gByStatus?.New || 0} new · ${stats.gByStatus?.Escalated || 0} escalated`,
      grad: "linear-gradient(135deg,#b45309,#f59e0b)",
    },
    {
      label: "Dev Projects",
      value: stats.totalProjects || 0,
      icon: "bi-building-fill",
      sub: `Budget: ${formatCurrency(stats.totalBudget || 0)}`,
      grad: "linear-gradient(135deg,#065f46,#059669)",
    },
    {
      label: "Beneficiaries",
      value: stats.totalBeneficiaries || 0,
      icon: "bi-heart-fill",
      sub: "Scheme beneficiaries",
      grad: "linear-gradient(135deg,#7c3aed,#a855f7)",
    },
    {
      label: "Contacts",
      value: stats.totalContacts || 0,
      icon: "bi-person-badge-fill",
      sub: "Workers & leaders",
      grad: "linear-gradient(135deg,#0e7490,#06b6d4)",
    },
    {
      label: "Tour Programs",
      value: stats.totalTours || 0,
      icon: "bi-map-fill",
      sub: "Constituency tours",
      grad: "linear-gradient(135deg,#be185d,#ec4899)",
    },
  ];

  return (
    <div style={{ background: "#f0f2f5", minHeight: "100vh", padding: "24px" }}>
      {/* ── Header ── */}
      <div className="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-3">
        <div>
          <h3 className="fw-bold mb-1" style={{ color: "#1a3c5e" }}>
            Welcome back, {user?.name?.split(" ")[0]}! 👋
          </h3>
          <p className="text-muted mb-0 small">
            Here's what's happening in your constituency today.
          </p>
        </div>
        <div className="text-end">
          <div
            className="fw-bold"
            style={{ color: "#1a3c5e", fontSize: "22px" }}
          >
            {timeStr}
          </div>
          <div className="small text-muted">
            {dayName}, {dateStr}
          </div>
        </div>
      </div>

      {/* ── Stat Cards ── */}
      <div className="row g-3 mb-3">
        {statCards.map((card, i) => (
          <div key={i} className="col-6 col-md-4 col-lg-2">
            <div
              className="card border-0 rounded-4 shadow-sm h-100"
              style={{ background: card.grad }}
            >
              <div className="card-body p-3">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <div className="text-white text-opacity-75 small mb-1">
                      {card.label}
                    </div>
                    <div
                      className="text-white fw-bold"
                      style={{ fontSize: "26px" }}
                    >
                      {card.value}
                    </div>
                  </div>
                  <div
                    className="rounded-3 d-flex align-items-center justify-content-center"
                    style={{
                      width: 42,
                      height: 42,
                      background: "rgba(255,255,255,0.2)",
                      flexShrink: 0,
                    }}
                  >
                    <i className={`bi ${card.icon} text-white fs-5`}></i>
                  </div>
                </div>
                <div
                  className="text-white text-opacity-75 mt-2"
                  style={{ fontSize: "11px" }}
                >
                  {card.sub}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Charts Row ── */}
      <div className="row g-3 mb-3">
        {/* Grievance Status */}
        <div className="col-12 col-lg-5">
          <div className="card border-0 rounded-4 shadow-sm h-100">
            <div className="card-body p-4">
              <h6 className="fw-bold mb-3" style={{ color: "#1a3c5e" }}>
                <i className="bi bi-bar-chart-fill me-2 text-warning"></i>
                Grievance Status
              </h6>
              {Object.entries(stats.gByStatus || {}).length === 0 ? (
                <div className="text-muted small text-center py-3">
                  No grievances yet
                </div>
              ) : (
                Object.entries(stats.gByStatus || {}).map(([status, count]) => {
                  const pct = Math.round(
                    (count / (stats.totalGrievances || 1)) * 100,
                  );
                  const c = statusColors[status] || {
                    bg: "#f3f4f6",
                    color: "#374151",
                  };
                  return (
                    <div key={status} className="mb-3">
                      <div className="d-flex justify-content-between mb-1">
                        <span
                          className="small fw-medium"
                          style={{ color: c.color }}
                        >
                          {status}
                        </span>
                        <div className="d-flex gap-2">
                          <span className="fw-bold small">{count}</span>
                          <span
                            className="text-muted"
                            style={{ fontSize: "11px" }}
                          >
                            {pct}%
                          </span>
                        </div>
                      </div>
                      <div
                        className="rounded-pill"
                        style={{ height: 8, background: "#f0f2f5" }}
                      >
                        <div
                          className="rounded-pill h-100"
                          style={{
                            width: `${pct}%`,
                            background: c.color,
                            transition: "width 1s",
                          }}
                        ></div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Priority */}
        <div className="col-12 col-md-6 col-lg-3">
          <div className="card border-0 rounded-4 shadow-sm h-100">
            <div className="card-body p-4">
              <h6 className="fw-bold mb-3" style={{ color: "#1a3c5e" }}>
                <i className="bi bi-flag-fill me-2 text-danger"></i>Priority
              </h6>
              {["CRITICAL", "HIGH", "MEDIUM", "LOW"].map((p) => {
                const count = stats.gByPriority?.[p] || 0;
                const pct = Math.round(
                  (count / (stats.totalGrievances || 1)) * 100,
                );
                return (
                  <div key={p} className="mb-3">
                    <div className="d-flex justify-content-between mb-1">
                      <div className="d-flex align-items-center gap-2">
                        <div
                          className="rounded-circle"
                          style={{
                            width: 10,
                            height: 10,
                            background: priorityColors[p],
                          }}
                        ></div>
                        <span className="small fw-medium">{p}</span>
                      </div>
                      <span className="fw-bold small">{count}</span>
                    </div>
                    <div
                      className="rounded-pill"
                      style={{ height: 6, background: "#f0f2f5" }}
                    >
                      <div
                        className="rounded-pill h-100"
                        style={{
                          width: `${pct}%`,
                          background: priorityColors[p],
                        }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Project Status */}
        <div className="col-12 col-md-6 col-lg-4">
          <div className="card border-0 rounded-4 shadow-sm h-100">
            <div className="card-body p-4">
              <h6 className="fw-bold mb-3" style={{ color: "#1a3c5e" }}>
                <i className="bi bi-building me-2 text-success"></i>Project
                Status
              </h6>
              {Object.entries(stats.pByStatus || {}).length === 0 ? (
                <div className="text-muted small text-center py-3">
                  No projects yet
                </div>
              ) : (
                <div className="d-flex flex-wrap gap-2">
                  {Object.entries(stats.pByStatus || {}).map(
                    ([status, count]) => (
                      <div
                        key={status}
                        className="rounded-3 px-3 py-2"
                        style={{
                          background: `${projectStatusColors[status]}22`,
                          border: `1px solid ${projectStatusColors[status]}44`,
                        }}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <div
                            className="rounded-circle"
                            style={{
                              width: 8,
                              height: 8,
                              background: projectStatusColors[status],
                            }}
                          ></div>
                          <div>
                            <div
                              className="small fw-medium"
                              style={{ color: projectStatusColors[status] }}
                            >
                              {status}
                            </div>
                            <div className="fw-bold small">{count}</div>
                          </div>
                        </div>
                      </div>
                    ),
                  )}
                </div>
              )}
              <div
                className="mt-3 p-3 rounded-3"
                style={{ background: "#f0fdf4" }}
              >
                <div className="small text-muted">Total Budget</div>
                <div className="fw-bold fs-5" style={{ color: "#15803d" }}>
                  {formatCurrency(stats.totalBudget || 0)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Recent Rows ── */}
      <div className="row g-3">
        {/* Recent Grievances */}
        <div className="col-12 col-lg-7">
          <div className="card border-0 rounded-4 shadow-sm">
            <div className="card-body p-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-bold mb-0" style={{ color: "#1a3c5e" }}>
                  <i className="bi bi-clock-history me-2 text-warning"></i>
                  Recent Grievances
                </h6>
                <a
                  href="/admin/grievances"
                  className="btn btn-sm btn-outline-primary rounded-pill px-3"
                >
                  View All
                </a>
              </div>
              {(stats.recentGrievances || []).length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <i className="bi bi-chat-left-text fs-2 d-block mb-2"></i>
                  No grievances yet
                </div>
              ) : (
                (stats.recentGrievances || []).map((g) => {
                  const c = statusColors[g.current_status] || {
                    bg: "#f3f4f6",
                    color: "#374151",
                  };
                  return (
                    <div
                      key={g.grievance_id}
                      className="d-flex align-items-center gap-3 py-2 border-bottom"
                    >
                      <div
                        className="rounded-3 d-flex align-items-center
                          justify-content-center flex-shrink-0"
                        style={{ width: 40, height: 40, background: c.bg }}
                      >
                        <i
                          className="bi bi-chat-left-text"
                          style={{ color: c.color }}
                        ></i>
                      </div>
                      <div className="flex-grow-1 overflow-hidden">
                        <div className="fw-medium small text-truncate">
                          {g.title}
                        </div>
                        <div
                          className="text-muted"
                          style={{ fontSize: "11px" }}
                        >
                          {g.ticket_no} • {g.reporter_name || "Unknown"}
                        </div>
                      </div>
                      <div className="text-end flex-shrink-0">
                        <span
                          className="badge rounded-pill small"
                          style={{ background: c.bg, color: c.color }}
                        >
                          {g.current_status}
                        </span>
                        <div
                          className="text-muted mt-1"
                          style={{ fontSize: "10px" }}
                        >
                          {g.created_at?.split(" ")[0]}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Recent Tours */}
        <div className="col-12 col-lg-5">
          <div className="card border-0 rounded-4 shadow-sm">
            <div className="card-body p-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-bold mb-0" style={{ color: "#1a3c5e" }}>
                  <i
                    className="bi bi-map me-2"
                    style={{ color: "#be185d" }}
                  ></i>
                  Recent Tours
                </h6>
                <a
                  href="/admin/tours"
                  className="btn btn-sm btn-outline-primary rounded-pill px-3"
                >
                  View All
                </a>
              </div>
              {(stats.recentTours || []).length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <i className="bi bi-map fs-2 d-block mb-2"></i>No tours yet
                </div>
              ) : (
                (stats.recentTours || []).map((t) => (
                  <div
                    key={t.tourid}
                    className="d-flex align-items-center gap-3 py-2 border-bottom"
                  >
                    <div
                      className="rounded-3 d-flex align-items-center
                        justify-content-center flex-shrink-0"
                      style={{ width: 40, height: 40, background: "#fdf2f8" }}
                    >
                      <i
                        className="bi bi-geo-alt-fill"
                        style={{ color: "#be185d" }}
                      ></i>
                    </div>
                    <div className="flex-grow-1 overflow-hidden">
                      <div className="fw-medium small text-truncate">
                        {t.tourname}
                      </div>
                      <div className="text-muted" style={{ fontSize: "11px" }}>
                        Ref: {t.reference}
                      </div>
                    </div>
                    <div className="text-end flex-shrink-0">
                      <span
                        className={`badge rounded-pill small ${
                          t.isattending
                            ? "bg-success-subtle text-success"
                            : "bg-secondary-subtle text-secondary"
                        }`}
                      >
                        {t.isattending ? "Attending" : "Not attending"}
                      </span>
                      <div
                        className="text-muted mt-1"
                        style={{ fontSize: "10px" }}
                      >
                        {t.tourdate?.split(" ")[0] || "—"}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
